import useAxios from "axios-hooks";
import ImportJobForm from "./ImportJobForm";
import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { useState, useEffect } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { UsersResDto } from "../../api/Api";
import AutocompleteInput from "../../components/input/AutoCompleteInput";
import { MembersTable } from "../members/Members";
import moment from "moment";
import PaginationButtons from "../../components/pagination/PaginationButtons";

export default function ImportJobs() {
  const outletContext = useOutletContext() as any;
  const navigate = useNavigate();

  const [searchString, setSearchString] = useState(undefined as any);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [{ data, loading, error }, getImportJobs] = useAxios<UsersResDto>({
    url: "/import-jobs",
    params: {
      page,
      limit: pageSize,
    },
  });

  useEffect(() => {
    outletContext?.setTitleComponent(
      <div className="flex items-center justify-center gap-2">
        {/* <ArrowUpTrayIcon className="h-8 w-8"></ArrowUpTrayIcon> */}
        Import Jobs
      </div>
    );
    if (searchString !== undefined) {
      getImportJobs();
    }
  }, [searchString]);

  return (
    <div className="flex flex-col gap-2 p-5">
      <div className="flex">
        <div className="mb-2">
          <AutocompleteInput
            onComplete={(input: string) => {
              setSearchString(input);
            }}
            placeHolder="Search..."
            className="bg-base-200"
          ></AutocompleteInput>
        </div>
        <div className="ml-auto">
          <button
            className="btn btn-sm ml-4"
            onClick={() => {
              (document?.getElementById("import_job") as any)?.showModal();
            }}
          >
            <ArrowUpTrayIcon className="h-4" />
            Start New Import Job
          </button>
        </div>
      </div>
      {!loading ? (
        <div className="flex gap-4 flex-col">
          <ImportJobsTable data={data?.docs}></ImportJobsTable>
          <PaginationButtons
            page={page}
            setPage={setPage}
            pageCount={data?.totalPages || 1}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </div>
      ) : (
        <div className="h-[20vh] w-full flex items-center justify-center">
          <span className="loading loading-spinner loading-md"></span>
        </div>
      )}

      <dialog id="import_job" className="modal ">
        <div className="modal-box min-w-[50vw]">
          <h3 className="font-bold text-lg pb-2">Create import job</h3>
          <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">
              ✕
            </button>
          </form>
          <div>Upload a CSV file to create users.</div>
          <div className="text-sm pt-4 pb-1 font-bold">
            The CSV should have the following structure
          </div>
          <div className="border dark:border-white dark:border-opacity-20 p-4 rounded-lg bg-base-200">
            username,email,temporary_password,role,send_invitation
            student1,student1@sentry.security,P@ssw0rd,STUDENT,true
            student2,student2@sentry.security,P@ssw0rd,STUDENT,false
          </div>
          <div className="bg-warning text-yellow-900 p-2 rounded-lg mt-4 text-sm">
            <span className="font-bold text-lg">Attention</span>
            <br></br>When the 'send_invitation' field is set to true, the user
            will receive an email with a temporary password. If set to false,
            the user will not receive an email, and the 'temporary_password'
            field is then required to be set.
          </div>
          <div className="mt-6">
            <ImportJobForm
              onSuccess={(data: any) => {
                navigate(`/import-jobs/${data?.data?._id}`);
              }}
            ></ImportJobForm>
          </div>
        </div>
      </dialog>
    </div>
  );
}

export function ImportJobsTable({ data }: { data: any }) {
  const navigate = useNavigate();

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "CREATED":
        return <span className="badge badge-info">Created</span>;
      case "COMPLETED":
        return <span className="badge badge-success">Completed</span>;
      case "FAILED":
        return <span className="badge badge-error">Failed</span>;
      default:
        return <span className="badge badge-neutral">{status}</span>;
    }
  };

  return (
    <div className="overflow-x-auto rounded-xl border border-base-300 bg-base-100 table-container">
      <table className="table table-md  w-full">
        <thead className="bg-base-200 text-base-content sticky top-0 z-10">
          <tr>
            <th className="text-sm ">Job</th>
            <th className="text-sm">Name</th>
            <th className="text-sm text-center">Records</th>
            <th className="text-sm text-center text-success">Success</th>
            <th className="text-sm text-center text-error">Failed</th>
            <th className="text-sm text-center">Status</th>
            <th className="text-sm text-center">Created</th>
          </tr>
        </thead>
        <tbody>
          {data?.map((job: any) => {
            const successCount = job?.success?.length || 0;
            const failCount = job?.fail?.length || 0;
            const total = job?.records?.length || 0;

            return (
              <tr
                key={job._id}
                className="hover:bg-base-300 transition cursor-pointer"
                onClick={() => navigate(`/import-jobs/${job._id}`)}
              >
                <td className="max-w-[150px] text-xs text-wrap break-all text-base-content/70">
                  <div className="tooltip" data-tip={job._id}>
                    {job._id.slice(0, 6)}...{job._id.slice(-4)}
                  </div>
                </td>
                <td className="font-medium">{job.name}</td>
                <td className="text-center">{total}</td>
                <td className="text-center text-success">{successCount}</td>
                <td className="text-center text-error">{failCount}</td>
                <td className="text-center">{getStatusBadge(job.status)}</td>
                <td className="text-center text-sm opacity-70">
                  {moment(job.createdAt).fromNow()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
